/*
 * Copyright 2026 Sony Corporation
 */

import { SCENE_CONTENT_TYPE } from '@ips-device-verse/twin-service/schema/scenes';
import { UPLOAD_TYPES, type Asset, type UploadType } from '@ips-device-verse/twin-service/schema/assets';

import type { ApiClient } from './api-client';

/** By extension, because that is all a dropped file reliably says about itself: `.glb` and `.spz` have no registered MIME type browsers agree on. */
const BY_EXTENSION: Record<string, UploadType> = {
  glb: 'model/gltf-binary',
  babylon: SCENE_CONTENT_TYPE,
  spz: 'application/x-spz',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
};

export const ACCEPTED_EXTENSIONS = Object.keys(BY_EXTENSION).map((extension) => `.${extension}`);

const extensionOf = (name: string): string => name.slice(name.lastIndexOf('.') + 1).toLowerCase();

export const uploadTypeOf = (file: File): UploadType | undefined => BY_EXTENSION[extensionOf(file.name)];

/** Why a file will not be accepted, before anything is sent. */
export const refusalOf = (file: File): string | undefined => {
  const type = uploadTypeOf(file);
  if (!type) {
    return `${file.name} is not a ${ACCEPTED_EXTENSIONS.join(', ')} file.`;
  }
  const { max_bytes: max } = UPLOAD_TYPES[type];
  if (file.size > max) {
    return `${file.name} is larger than ${Math.round(max / 1024 / 1024)} MB.`;
  }
  return file.size === 0 ? `${file.name} is empty.` : undefined;
};

const nameOf = (file: File): string => {
  const stem = file.name.includes('.') ? file.name.slice(0, file.name.lastIndexOf('.')) : file.name;
  return (stem.trim() || file.name).slice(0, 64);
};

/** XHR rather than fetch: fetch cannot report upload progress, and a video takes minutes. */
const put = (url: string, file: File, contentType: string, onProgress?: (fraction: number) => void): Promise<void> =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    request.setRequestHeader('Content-Type', contentType);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.(event.loaded / event.total);
      }
    };
    request.onload = () =>
      request.status >= 200 && request.status < 300
        ? resolve()
        : reject(new Error(`The upload failed (${request.status}).`));
    request.onerror = () => reject(new Error('The upload failed.'));
    request.send(file);
  });

const THUMBNAIL_EDGE = 480;
const THUMBNAIL_TIMEOUT_MS = 5000;

const within = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  Promise.race([promise, new Promise<T>((_, reject) => setTimeout(() => reject(new Error('timed out')), ms))]);

/**
 * A frame from early in the video, as base64 JPEG.
 *
 * Undefined when the browser cannot decode it (HEVC on most desktops), which
 * costs the tile its picture and nothing else.
 */
const videoThumbnail = async (file: File): Promise<string | undefined> => {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  try {
    video.muted = true;
    video.preload = 'auto';
    video.src = url;
    await within(
      new Promise((resolve, reject) => {
        video.onloadeddata = resolve;
        video.onerror = reject;
      }),
      THUMBNAIL_TIMEOUT_MS
    );
    video.currentTime = Math.min(1, video.duration / 2);
    await within(new Promise((resolve) => (video.onseeked = resolve)), THUMBNAIL_TIMEOUT_MS);

    const scale = THUMBNAIL_EDGE / Math.max(video.videoWidth, video.videoHeight);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
  } catch {
    return undefined;
  } finally {
    video.removeAttribute('src');
    URL.revokeObjectURL(url);
  }
};

/** Create, upload, publish (devplan §5.7). An upload that fails midway stays listed, as unfinished, until deleted. */
export const uploadAsset = async (
  api: ApiClient,
  file: File,
  onProgress?: (fraction: number) => void
): Promise<Asset> => {
  const refusal = refusalOf(file);
  const contentType = uploadTypeOf(file);
  if (refusal || !contentType) {
    throw new Error(refusal);
  }

  const { asset, upload_url: url } = await api.createAsset({
    name: nameOf(file),
    content_type: contentType,
    bytes: file.size,
  });
  await put(url, file, contentType, onProgress);

  const thumbnail = asset.kind === 'video' ? await videoThumbnail(file) : undefined;
  return api.publishAsset(asset.asset_id, thumbnail ? { thumbnail } : {});
};
